import request from '@/api/config.js'

// Добавление организации без загрузки файла
// export function createOrg(data) {
//   return request({
//     url: `org/create`,
//     method: 'post',
//     data,
//   })
// }

/**
 * Создает новую организацию с загрузкой файла
 * @param {Object} data - Данные для создания организации
 * @param {Object} data.orgData - Данные организации (название, описание и т.д.)
 * @param {Object} data.employeeData - Данные сотрудника-администратора организации
 * @returns {Promise} Промис с ответом сервера
 */
export function createOrg(data) {
  const formData = new FormData()

  // Добавляем данные организации
  for (const key in data.orgData) {
    formData.append(key, data.orgData[key])
  }

  // Добавляем данные сотрудника
  formData.append('employee', JSON.stringify(data.employeeData))

  return request({
    url: 'org',
    method: 'post',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}

/**
 * Получает список всех организаций
 * @returns {Promise} Промис с массивом организаций
 */
export function getAllOrgs() {
  return request({
    url: `org`,
    method: 'get',
  })
}

/**
 * Получает данные одной организации по её ID
 * @param {number|string} id - ID организации
 * @returns {Promise} Промис с данными организации
 */
export function getOrgById(id) {
  return request({
    url: `org/${id}`,
    method: 'get',
  })
}

/**
 * Данные организации для сайдбара: id, name, logo и logoApplyCabinet.
 * @param {number|string} id - ID организации
 * @returns {Promise<{ data: { id, name, logo: string|null, logoApplyCabinet: boolean, accentColor: string, accentApplyCabinet: boolean, mainColor: string, mainApplyCabinet: boolean } }>}
 */
export function getOrgForSidebar(id) {
  return request({
    url: `org/${id}/sidebar`,
    method: 'get',
  })
}

/**
 * Тип бизнеса и настройки ЛК (подтип, service_place, activity_field).
 * Без departments, employees, director, record_counter, tariffs.
 * @param {number|string} id - ID организации
 * @returns {Promise}
 */
export function getOrgBusinessType(id) {
  return request({
    url: `org/${id}/business-type`,
    method: 'get',
  })
}

/**
 * Настройки личного кабинета организации.
 * @param {number|string} id - ID организации
 * @returns {Promise<{ data: { autoStatusSwitch: boolean, scheduleEnabled: boolean, allowParallelBooking: boolean, logo: string|null, logoApplyCabinet: boolean, logoApplyOnline: boolean, canUploadLogo: boolean, accentColor: string, accentApplyCabinet: boolean, accentApplyOnline: boolean, mainColor: string, mainApplyCabinet: boolean, mainApplyOnline: boolean } }>}
 */
export function getOrgLkSettings(id) {
  return request({
    url: `org/${id}/lk-settings`,
    method: 'get',
  })
}

/**
 * Обновить настройки личного кабинета организации.
 * @param {number|string} id - ID организации
 * @param {{ autoStatusSwitch?: boolean, scheduleEnabled?: boolean, allowParallelBooking?: boolean, logoApplyCabinet?: boolean, logoApplyOnline?: boolean, accentColor?: string, accentApplyCabinet?: boolean, accentApplyOnline?: boolean, mainColor?: string, mainApplyCabinet?: boolean, mainApplyOnline?: boolean }} data
 */
export function updateOrgLkSettings(id, data) {
  return request({
    url: `org/${id}/lk-settings`,
    method: 'patch',
    data,
  })
}

/**
 * Настройки онлайн-записи организации.
 * @param {number|string} id - ID организации
 * @returns {Promise<{ data: { allowPostSelectionOnline: boolean, postSelectionStepTitle: string|null, scheduleEnabled: boolean, onlineSelectsEmployee: boolean, priceHiding: boolean, priceHidingDepartments: Array<{ id: number, name: string, priceHiding: boolean }> } }>}
 */
export function getOrgOnlineRecordSettings(id) {
  return request({
    url: `org/${id}/online-record-settings`,
    method: 'get',
  })
}

/**
 * Обновить настройки онлайн-записи организации.
 *
 * `priceHiding` без `priceHidingDepartmentId` переключает скрытие стоимости
 * во всех филиалах организации сразу (мастер-переключатель).
 *
 * @param {number|string} id - ID организации
 * @param {{ allowPostSelectionOnline?: boolean, postSelectionStepTitle?: string|null, onlineSelectsEmployee?: boolean, priceHiding?: boolean, priceHidingDepartmentId?: number }} data
 */
export function updateOrgOnlineRecordSettings(id, data) {
  return request({
    url: `org/${id}/online-record-settings`,
    method: 'patch',
    data,
  })
}

/**
 * Обновляет данные организации с возможностью загрузки файла
 * @param {number|string} id - ID организации
 * @param {Object} data - Объект с данными для обновления
 * @returns {Promise} Промис с ответом сервера
 */
export function updateOrg(id, data) {
  const formData = new FormData()

  for (const key in data) {
    formData.append(key, data[key])
  }

  return request({
    url: `org/${id}`,
    method: 'put',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}

export function uploadOrgLogo(id, file) {
  const formData = new FormData()
  formData.append('logo', file)

  return request({
    url: `org/${id}/logo`,
    method: 'put',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 15000,
    data: formData,
  })
}

export function deleteOrgLogo(id) {
  return request({
    url: `org/${id}/logo`,
    method: 'delete',
  })
}

/**
 * Удаляет организацию по ID
 * Удаляет организацию и все связанные данные. Опционально — также пользователей организации.
 * @param {number|string} id - ID организации для удаления
 * @param {Object} [options] - Опции удаления
 * @param {boolean} [options.deleteUsers=false] - true: также удалить пользователей организации (директор, сотрудники)
 * @returns {Promise} Промис с ответом сервера
 */
export function deleteOrg(id, options = {}) {
  const params = {}
  if (options.deleteUsers === true) {
    params.deleteUsers = 'true'
  }
  return request({
    url: `org/${id}`,
    method: 'delete',
    params,
  })
}

/**
 * Получает сводку данных, которые будут удалены при удалении организации
 * Возвращает количество филиалов, клиентов, сотрудников, записей, услуг и категорий
 * @param {number|string} id - ID организации
 * @returns {Promise} Промис с объектом, содержащим количество связанных данных
 * @returns {Promise<Object>} response.data - Объект со сводкой:
 * @returns {number} response.data.departments - Количество филиалов
 * @returns {number} response.data.clients - Количество клиентов
 * @returns {number} response.data.employees - Количество сотрудников
 * @returns {number} response.data.positions - Количество должностей
 * @returns {number} response.data.records - Количество записей
 * @returns {number} response.data.services - Количество услуг
 * @returns {number} response.data.serviceCategories - Количество категорий услуг
 */
export function getOrgDeleteSummary(id) {
  return request({
    url: `org/${id}/delete-summary`,
    method: 'get',
  })
}

/**
 * Получает список всех организаций с короткой информацией
 * @returns {Promise} Промис с массивом организаций
 */
export function getAllOrgsShort() {
  return request({
    url: `org/get-all-orgs-short`,
    method: 'get',
  })
}

/**
 * Получает все организации, которые можно показывать на странице партнеров
 * @returns {Promise} Промис с массивом организаций
 */
export function getAllPartners() {
  return request({
    url: `org/get-all-partners`,
    method: 'get',
  })
}

/**
 * Возвращает организацию по id с данными для страницы партнеровов
 * @returns {Promise} Промис с объектов партнера
 */
export function getPartner(orgId) {
  return request({
    url: `org/${orgId}/get-partner`,
    method: 'get',
  })
}

/**
 * Обновляет статус видимости организации на странице партнеров
 * @returns {Promise} Промис с результатом обновления орги
 */
export function toggleVisibleStatusOrg(orgId, formData) {
  return request({
    url: `org/${orgId}/toggle-visible-status-org`,
    method: 'patch',
    data: formData,
  })
}

/**
 * Обновляет данные орги для страницы партнеров
 * @returns {Promise} Промис с результатом обновления орги
 */
export function updatePartner(orgId, formData) {
  return request({
    url: `org/${orgId}/update-partner`,
    method: 'put',
    data: formData,
  })
}

export function getOrgLkAccess(id) {
  return request({
    url: `org/${id}/lk-access`,
    method: 'get',
  })
}

export function toggleOrgLk(id, enabledLk) {
  return request({
    url: `org/${id}/toggle-lk`,
    method: 'patch',
    data: { enabledLk },
  })
}

export function requestOrgLkRestore(id) {
  return request({
    url: `org/${id}/lk-access/restore`,
    method: 'post',
    timeout: 15000,
  })
}
